import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import { useComponentTranslations } from '../hooks/useTranslations';

const About = () => {
  const { t, loading } = useComponentTranslations('about');

  if (loading) {
    return (
      <section id="about" className="py-20 bg-gradient-to-br from-gray-50 to-champagne-50 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-12 bg-gray-200 rounded-lg animate-pulse mb-8"></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-4">
              <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div className="h-64 bg-gray-200 rounded-lg animate-pulse"></div>
          </div>
        </div>
      </section>
    );
  }

  const values = [
    {
      title: t('value_excellence_title', 'Excellence'),
      description: t('value_excellence_desc', 'We deliver superior results through commitment to quality and continuous improvement')
    },
    {
      title: t('value_integrity_title', 'Integrity'),
      description: t('value_integrity_desc', 'Trust and transparency form the foundation of our client relationships')
    },
    {
      title: t('value_innovation_title', 'Innovation'),
      description: t('value_innovation_desc', 'We embrace change and drive continuous improvement in business operations')
    },
    {
      title: t('value_partnership_title', 'Partnership'),
      description: t('value_partnership_desc', 'We work collaboratively with our clients to achieve shared success')
    }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-gray-50 to-champagne-50 scroll-mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            {t('title', 'About Dreamline Operations')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('subtitle', 'Management consulting and business operations support for sustainable growth')}
          </p>
        </motion.div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <h3 className="text-3xl font-bold text-gray-900">
              {t('mission_title', 'Our Mission')}
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed">
              {t('mission_desc', 'Dreamline Operations LLC provides comprehensive management consulting and business operations support to help companies streamline processes, strengthen operations, and achieve sustainable success. We specialize in operational strategy, process optimization, and business management solutions.')}
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              {t('approach_desc', 'Our consultative approach combines industry expertise with proven methodologies, delivering customized solutions that drive operational excellence and business growth. We partner with our clients to optimize workflows, improve efficiency, and strengthen organizational capabilities.')}
            </p>
            
            {/* Key Points */}
            <div className="space-y-3 mt-8">
              {[
                t('point_process', 'Process optimization and workflow improvement'),
                t('point_strategic', 'Strategic management consulting solutions'),
                t('point_operational', 'Operational excellence initiatives'),
                t('point_business', 'Business support and administrative services')
              ].map((point, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex items-center space-x-3"
                >
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="relative h-96 lg:h-[500px]"
          >
            <img
              src="https://images.unsplash.com/photo-1742119971773-57e0131095b0"
              alt="Professional Management Consultant"
              className="rounded-2xl shadow-2xl w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-evergreen/20 to-transparent rounded-2xl"></div>
          </motion.div>
        </div>

        {/* Values */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            {t('values_title', 'Our Core Values')}
          </h3>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('values_subtitle', 'The principles that guide our decisions and actions')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((value, index) => (
            <motion.div
              key={value.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="text-center bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              <h4 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h4>
              <p className="text-gray-600">{value.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;